import {
  RELEASES,
  REPLACE_RELEASE,
  ADD_RELEASE,
  UPDATE_RELEASE,
  PUBLISH_RELEASE,
  RESET,
  REMOVE_RELEASE_PACKAGE,
  ADD_PACKAGE_TO_RELEASE,
  CREATE_NEW_RELEASE,
  UNCOMMITTED_RELEASE,
  DELETE_UNCOMMITTED_RELEASE,
  COMMIT_RELEASE,
  UPDATE_UNCOMMITTED_RELEASE,
  UNCOMMITTED_RELEASE_REPLACE_PACKAGE,
  GROUP_RELEASE,
  CREATE_GROUP_RELEASE,
  UPDATE_GROUP_RELEASE,
  REMOVE_GROUP_RELEASE,
  REMOVE_GROUP_MEMBER,
  GROUP_RELEASE_MEMBER,
  GROUP_RELEASE_BY_ID,
  BETA_GROUP,
  ALPHA_GROUP,
} from '../../types';

import Release from '@/api/app-releases';

export const state = () => ({
  uncommittedRelease: null,
  releases: null,
  alphaGroup: null,
  betaGroup: null,
  releaseMember: null,
  groupReleaseId: null,
});

export const getters = {
  [RELEASES]({ releases }) {
    return releases;
  },
  [UNCOMMITTED_RELEASE]({ uncommittedRelease }) {
    return uncommittedRelease;
  },
  [BETA_GROUP]({ betaGroup }) {
    return betaGroup;
  },
  [ALPHA_GROUP]({ alphaGroup }) {
    return alphaGroup;
  },
  [GROUP_RELEASE_MEMBER]({ releaseMember }) {
    return releaseMember;
  },
  [GROUP_RELEASE_BY_ID]({ groupReleaseId }) {
    return groupReleaseId;
  },
};

export const mutations = {
  [UNCOMMITTED_RELEASE](stateObj, release) {
    stateObj.uncommittedRelease = release;
  },
  [UPDATE_UNCOMMITTED_RELEASE](stateObj, newData) {
    stateObj.uncommittedRelease = {
      ...stateObj.uncommittedRelease,
      ...newData,
    };
  },
  [UNCOMMITTED_RELEASE_REPLACE_PACKAGE](stateObj, newPackage) {
    const packages = stateObj.uncommittedRelease?.packages.map(pkg => {
      if (pkg.token === newPackage.token) {
        return newPackage;
      }
      return pkg;
    });

    stateObj.uncommittedRelease = {
      ...stateObj.uncommittedRelease,
      packages,
    };
  },
  [DELETE_UNCOMMITTED_RELEASE](stateObj) {
    stateObj.uncommittedRelease = null;
  },
  [RELEASES](stateObj, releases) {
    stateObj.releases = releases;
  },
  [REPLACE_RELEASE](stateObj, newRelease) {
    const updatedReleases = stateObj.releases.map(release => {
      if (release.token === newRelease.token) {
        return {
          ...newRelease,
          isLastRelease: release.isLastRelease,
        };
      }
      return release;
    });

    stateObj.releases = updatedReleases;
  },
  [RESET](stateObj) {
    Object.assign(stateObj, state());
  },
  [ADD_RELEASE](stateObj, newRelease) {
    if (stateObj.releases[0]) {
      stateObj.releases[0].isLastRelease = false;
    }
    newRelease.isLastRelease = true;
    stateObj.releases.unshift(newRelease);
  },
  [REMOVE_RELEASE_PACKAGE](stateObj, { releaseId, packageToken }) {
    const updatedReleasesList = stateObj.releases.map(release => {
      if (release.token === releaseId) {
        release.packages = release.packages.filter(
          pkg => pkg.token !== packageToken,
        );
      }
      return release;
    });

    stateObj.releases = updatedReleasesList;
  },
  [ADD_PACKAGE_TO_RELEASE](stateObj, { releaseId, packageItem }) {
    stateObj.releases = stateObj.releases.map(release => {
      if (release.token === releaseId) {
        release.packages.unshift(packageItem);
      }
      return release;
    });
  },
  [GROUP_RELEASE](stateObj, groupRelease) {
    const enabledGroups = groupRelease?.filter(group => group.enabled);
    stateObj.betaGroup = enabledGroups?.filter(group => group.name === 'beta');
    stateObj.alphaGroup = enabledGroups?.filter(group => group.name !== 'beta');
  },
  [GROUP_RELEASE_MEMBER](stateObj, releaseMember) {
    stateObj.releaseMember = releaseMember;
  },
  [GROUP_RELEASE_BY_ID](stateObj, groupReleaseId) {
    stateObj.groupReleaseId = groupReleaseId;
  },
};

export const actions = {
  [CREATE_NEW_RELEASE]({ commit }, packageName) {
    return Release.createRelease({ packageName }).then(release => {
      commit(UNCOMMITTED_RELEASE, release);
    });
  },
  [DELETE_UNCOMMITTED_RELEASE]({ commit, state }, packageName) {
    return Release.deleteRelease({
      packageName,
      token: state.uncommittedRelease.token,
    }).then(() => {
      commit(DELETE_UNCOMMITTED_RELEASE);
    });
  },
  [UNCOMMITTED_RELEASE]({ commit }, packageName) {
    return Release.getUncommittedRelease({ packageName }).then(release => {
      commit(UNCOMMITTED_RELEASE, release);
    });
  },
  [UPDATE_UNCOMMITTED_RELEASE]({ commit }, newData) {
    if (newData) commit(UPDATE_UNCOMMITTED_RELEASE, newData);
  },
  [UNCOMMITTED_RELEASE_REPLACE_PACKAGE]({ commit }, newPackage) {
    if (newPackage) commit(UNCOMMITTED_RELEASE_REPLACE_PACKAGE, newPackage);
  },
  [COMMIT_RELEASE]({ commit, dispatch }, requestBody) {
    return Release.commitRelease(requestBody).then(({ release, warnings }) => {
      commit(ADD_RELEASE, release);
      commit(DELETE_UNCOMMITTED_RELEASE, requestBody.packageName);
      dispatch(RELEASES, requestBody.packageName);
      return { release, warnings };
    });
  },
  [RELEASES]({ commit }, packageName) {
    return Release.getRelease({ packageName }).then(releases => {
      commit(RELEASES, releases);
    });
  },
  [REPLACE_RELEASE]({ commit }, newRelease) {
    commit(REPLACE_RELEASE, newRelease);
  },
  [ADD_RELEASE]({ commit }, newRelease) {
    commit(ADD_RELEASE, newRelease);
  },
  [UPDATE_RELEASE]({ commit }, { queryParams, updateBody }) {
    const { token, packageName } = queryParams;
    return Release.updateRelease({
      packageName,
      releaseId: token,
      updateBody,
    }).then(({ release, message }) => {
      commit(REPLACE_RELEASE, release);
      return message;
    });
  },
  [PUBLISH_RELEASE]({ commit }, { token, packageName }) {
    return Release.publishRelease({
      releaseId: token,
      packageName,
    }).then(({ release, message }) => {
      commit(REPLACE_RELEASE, release);
      return message;
    });
  },
  [GROUP_RELEASE]({ commit }, packageName) {
    return Release.getReleaseGroup({ packageName }).then(groupRelease => {
      commit(GROUP_RELEASE, groupRelease);
    });
  },
  [CREATE_GROUP_RELEASE](
    { dispatch },
    { packageName, name, enabled, member_phones },
  ) {
    return Release.createGroup({
      packageName,
      name,
      enabled,
      member_phones,
    }).then(({ message }) => {
      dispatch(GROUP_RELEASE, packageName);
      return message;
    });
  },
  [UPDATE_GROUP_RELEASE](
    { dispatch },
    { packageName, id, name, enabled, member_phones },
  ) {
    return Release.updateGroup({
      packageName,
      id,
      name,
      enabled,
      member_phones,
    }).then(({ message }) => {
      dispatch(GROUP_RELEASE_MEMBER, { packageName, id });
      dispatch(GROUP_RELEASE, packageName);
      return message;
    });
  },
  [REMOVE_GROUP_RELEASE]({ dispatch }, { packageName, id }) {
    return Release.removeGroup({
      packageName,
      id,
    }).then(({ message }) => {
      dispatch(GROUP_RELEASE, packageName);
      return message;
    });
  },
  [GROUP_RELEASE_BY_ID]({ commit }, { packageName, id }) {
    return Release.getReleaseGroupById({ packageName, id }).then(
      groupReleaseId => {
        commit(GROUP_RELEASE_BY_ID, groupReleaseId);
      },
    );
  },
  [GROUP_RELEASE_MEMBER]({ commit }, { packageName, id }) {
    return Release.getReleaseMemberByID({
      packageName,
      id,
    }).then(body => {
      commit(GROUP_RELEASE_MEMBER, body);
    });
  },

  [REMOVE_GROUP_MEMBER]({ dispatch }, { packageName, id, phone_number }) {
    return Release.removeGroupMember({
      packageName,
      id,
      phone_number,
    }).then(({ message }) => {
      dispatch(GROUP_RELEASE_MEMBER, { packageName, id });
      return message;
    });
  },
};
export default {
  state,
  getters,
  mutations,
  actions,
};
